import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Tab, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  AiOutlineFundProjectionScreen, 
  AiOutlineUser, 
  AiOutlineTool,
  AiOutlineLink,
  AiOutlineLogout,
  AiOutlinePicture
} from 'react-icons/ai';
import { BsBriefcaseFill } from 'react-icons/bs';
import { FaGraduationCap } from 'react-icons/fa';
import ProjectsEditor from './ProjectsEditor';
import AboutEditor from './AboutEditor';
import SkillsEditor from './SkillsEditor';
import SocialEditor from './SocialEditor';
import PortfolioSettingsEditor from './PortfolioSettingsEditor';
import WorkExperienceEditor from './WorkExperienceEditor';
import EducationEditor from './EducationEditor';
import Particle from '../components/Particle';
import './AdminDashboard.css';

function Dashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    // Verify token (basic check)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      
      if (payload.exp && payload.exp < now) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="loading-spinner" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="admin-dashboard" style={{ minHeight: '100vh', position: 'relative' }}>
      <Particle />
      <Container fluid style={{ position: 'relative', zIndex: 1 }}>
        <div className="dashboard-header">
          <div className="d-flex justify-content-between align-items-center">
            <h1>🎨 Portfolio Admin Dashboard</h1>
            <Button className="logout-btn" onClick={handleLogout}>
              <AiOutlineLogout style={{ marginRight: '0.5rem' }} />
              Logout
            </Button>
          </div>
        </div>
        
        <Tab.Container defaultActiveKey="projects">
          <Row>
            <Col lg={3} md={4}>
              <div className="sidebar">
                <div className="sidebar-section mb-4">
                  <h5 className="sidebar-section-title">📋 Content Management</h5>
                  <Nav variant="pills" className="flex-column">
                    <Nav.Item>
                      <Nav.Link eventKey="projects">
                        <AiOutlineFundProjectionScreen size={20} />
                        <span>Projects</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="about">
                        <AiOutlineUser size={20} />
                        <span>About</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="work-experience">
                        <BsBriefcaseFill size={20} />
                        <span>Work Experience</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="education">
                        <FaGraduationCap size={20} />
                        <span>Education</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="skills">
                        <AiOutlineTool size={20} />
                        <span>Skills</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="social">
                        <AiOutlineLink size={20} />
                        <span>Social Links</span>
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
                <div className="sidebar-section">
                  <h5 className="sidebar-section-title">⚙️ Configuration</h5>
                  <Nav variant="pills" className="flex-column">
                    <Nav.Item>
                      <Nav.Link eventKey="settings">
                        <AiOutlinePicture size={20} />
                        <span>Images & Settings</span>
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </div>
              </div>
            </Col>
            
            <Col lg={9} md={8}>
              <div className="content-area">
                <Tab.Content>
                  <Tab.Pane eventKey="projects">
                    <ProjectsEditor />
                  </Tab.Pane>
                  <Tab.Pane eventKey="about">
                    <AboutEditor />
                  </Tab.Pane>
                  <Tab.Pane eventKey="work-experience">
                    <WorkExperienceEditor />
                  </Tab.Pane>
                  <Tab.Pane eventKey="education">
                    <EducationEditor />
                  </Tab.Pane>
                  <Tab.Pane eventKey="skills">
                    <SkillsEditor />
                  </Tab.Pane>
                  <Tab.Pane eventKey="social">
                    <SocialEditor />
                  </Tab.Pane>
                  <Tab.Pane eventKey="settings">
                    <PortfolioSettingsEditor />
                  </Tab.Pane>
                </Tab.Content>
              </div>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
    </div>
  );
}

export default Dashboard;

